import React from 'react';

// Custom (non-lucide) icon set for the Belonging Sessions dashboard.
// Intentionally avoids importing any third-party icon library so these can be copied
// across sister platforms.

const strokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round'
};

const Svg = ({ className = 'w-6 h-6', viewBox = '0 0 24 24', children, ...props }) => (
  <svg className={className} viewBox={viewBox} {...strokeProps} {...props}>
    {children}
  </svg>
);

export const ChevronRight = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M10 6l6 6-6 6" />
  </Svg>
);

export const Download = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 3v10" />
    <path d="M8 11l4 4 4-4" />
    <path d="M5 20h14" />
  </Svg>
);

export const BookOpen = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M3.5 6.5c0-1.1.9-2 2-2H11c1.1 0 2 .9 2 2v14c-1-.9-2.2-1.5-3.5-1.5H5.5c-1.1 0-2-.9-2-2v-10.5z" />
    <path d="M20.5 6.5c0-1.1-.9-2-2-2H13" />
    <path d="M13 20.5c1-.9 2.2-1.5 3.5-1.5h2c1.1 0 2-.9 2-2V6.5" />
    <path d="M8 8.5h3" opacity="0.6" />
    <path d="M8 11h3" opacity="0.6" />
  </Svg>
);

export const FileText = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M7 3h7l3 3v15a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z" />
    <path d="M14 3v4h4" />
    <path d="M8 11h8" opacity="0.6" />
    <path d="M8 14h8" opacity="0.6" />
    <path d="M8 17h5" opacity="0.6" />
  </Svg>
);

export const TrendingUp = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M3 17l6-6 4 4 7-7" />
    <path d="M14 8h6v6" />
  </Svg>
);

export const Home = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M4 11l8-7 8 7" />
    <path d="M6 10.5V20a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-9.5" />
  </Svg>
);

export const Lock = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M7 11V8a5 5 0 0 1 10 0v3" />
    <path d="M6 11h12v10H6z" />
    <path d="M12 16v2" opacity="0.6" />
  </Svg>
);

export const Mail = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M4 6h16v12H4z" />
    <path d="M4 7l8 6 8-6" />
  </Svg>
);

export const Send = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M3 11.5l18-8-6.5 18-3.2-7.1L3 11.5z" />
    <path d="M11.3 14.4L21 3.5" opacity="0.5" />
  </Svg>
);

export const Heart = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 21l-1.5-1.3C5.5 15.5 3 12.8 3 9.8 3 7.1 5.1 5 7.8 5c1.6 0 3.1.8 4.2 2.1C13.1 5.8 14.6 5 16.2 5 18.9 5 21 7.1 21 9.8c0 3-2.5 5.7-7.5 9.9L12 21z" />
  </Svg>
);

export const PenLine = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M4 20h4l10.5-10.5a2 2 0 0 0 0-2.8l-.2-.2a2 2 0 0 0-2.8 0L5 16v4z" />
    <path d="M13 6l5 5" />
  </Svg>
);

export const Star = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 2l2.6 6.1 6.4.6-4.9 4.2 1.5 6.3L12 16.9 6.4 19.2l1.5-6.3L3 8.7l6.4-.6L12 2z" />
  </Svg>
);

export const Calendar = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M7 3v3" />
    <path d="M17 3v3" />
    <path d="M4 7h16" />
    <path d="M5 6h14a1.5 1.5 0 0 1 1.5 1.5V20A2 2 0 0 1 18.5 22h-13A2 2 0 0 1 3.5 20V7.5A1.5 1.5 0 0 1 5 6z" />
    <path d="M7 11h4" opacity="0.6" />
    <path d="M7 14h7" opacity="0.6" />
  </Svg>
);

export const User = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <circle cx="12" cy="8" r="3" />
    <path d="M5 20a7 7 0 0 1 14 0" />
  </Svg>
);

export const FolderOpen = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M3.5 6.5h6l2 2h9a2 2 0 0 1 2 2v7.5a2 2 0 0 1-2 2h-15a2 2 0 0 1-2-2V8.5a2 2 0 0 1 2-2z" />
    <path d="M3.5 9h19" opacity="0.5" />
  </Svg>
);

export const Link2 = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M10 13a4 4 0 0 0 6 0l2-2a4 4 0 0 0-6-6l-1 1" />
    <path d="M14 11a4 4 0 0 0-6 0l-2 2a4 4 0 0 0 6 6l1-1" />
  </Svg>
);

export const Video = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M4 7h11a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" />
    <path d="M17 10l5-3v10l-5-3" />
  </Svg>
);

export const ExternalLink = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M14 4h6v6" />
    <path d="M10 14L20 4" />
    <path d="M20 14v6H4V4h6" />
  </Svg>
);

export const MapPin = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 22s7-5.2 7-12a7 7 0 0 0-14 0c0 6.8 7 12 7 12z" />
    <circle cx="12" cy="10" r="2" />
  </Svg>
);

export const CheckCircle = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8 12l2.5 2.5L16 9" />
  </Svg>
);

export const Clock = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v6l4 2" />
  </Svg>
);

export const Loader2 = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 3a9 9 0 1 0 9 9" />
  </Svg>
);

export const Play = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M10 8l7 4-7 4V8z" />
  </Svg>
);

export const PlayCircle = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M11 9l5 3-5 3V9z" />
  </Svg>
);

export const RefreshCw = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M20 12a8 8 0 0 1-14.5 4.5" />
    <path d="M4 12A8 8 0 0 1 18.5 7.5" />
    <path d="M18 3v5h-5" />
    <path d="M6 21v-5h5" />
  </Svg>
);

export const MessageCircle = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M21 12a8 8 0 0 1-8 8H7l-4 2 1.5-4.5A8 8 0 1 1 21 12z" />
  </Svg>
);

export const Sparkles = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 2l1.2 3.4L17 6.6l-3 2.3 1.1 3.6-3.1-2-3.1 2 1.1-3.6-3-2.3 3.8-1.2L12 2z" />
    <path d="M5 13l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7.7-2z" opacity="0.6" />
    <path d="M19 14l.6 1.6 1.6.6-1.6.6-.6 1.6-.6-1.6-1.6-.6 1.6-.6.6-1.6z" opacity="0.6" />
  </Svg>
);

export const Bell = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M18 16H6c1.2-1.2 2-2.6 2-4.5V10a4 4 0 0 1 8 0v1.5c0 1.9.8 3.3 2 4.5z" />
    <path d="M10 16a2 2 0 0 0 4 0" />
  </Svg>
);

export const AlertCircle = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v6" />
    <path d="M12 17h.01" />
  </Svg>
);

export const Megaphone = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M4 11v2" />
    <path d="M5 10l10-4v12l-10-4z" />
    <path d="M15 9l5-2v10l-5-2" />
    <path d="M7.5 14.5l1 4" />
  </Svg>
);

export const Wifi = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M5 10a10 10 0 0 1 14 0" />
    <path d="M8 13a6 6 0 0 1 8 0" />
    <path d="M11 16a2 2 0 0 1 2 0" />
    <circle cx="12" cy="18" r="1" fill="currentColor" stroke="none" />
  </Svg>
);

export const WifiOff = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M5 10a10 10 0 0 1 9.5-2" opacity="0.6" />
    <path d="M8 13a6 6 0 0 1 5.5-1" opacity="0.6" />
    <path d="M11 16a2 2 0 0 1 2 0" opacity="0.6" />
    <circle cx="12" cy="18" r="1" fill="currentColor" stroke="none" />
    <path d="M4 4l16 16" />
  </Svg>
);

export const Shield = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 3l8 4v6c0 5-3.4 8.5-8 10-4.6-1.5-8-5-8-10V7l8-4z" />
    <path d="M9.5 12l1.5 1.5L15 10" opacity="0.7" />
  </Svg>
);

export const Users = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <circle cx="9" cy="9" r="2.5" />
    <circle cx="16" cy="10" r="2" opacity="0.9" />
    <path d="M3.5 20a6 6 0 0 1 11 0" />
    <path d="M14 19a4.5 4.5 0 0 1 6 0" opacity="0.7" />
  </Svg>
);

export const Inbox = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M4 4h16v12H4z" />
    <path d="M4 16l4 4h8l4-4" />
    <path d="M9 14l3 3 3-3" opacity="0.6" />
  </Svg>
);

export const Edit3 = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4L16.5 3.5z" />
  </Svg>
);

export const Trash2 = ({ className = 'w-6 h-6', ...props }) => (
  <Svg className={className} {...props}>
    <path d="M4 7h16" />
    <path d="M9 7V5h6v2" />
    <path d="M7 7l1 14h8l1-14" />
    <path d="M10 11v6" opacity="0.6" />
    <path d="M14 11v6" opacity="0.6" />
  </Svg>
);
